import template from "./user-entities.html";
import "./user-entities.less";
import {
	differenceWith,
	intersectionWith,
	forEach,
	remove,
	some
} from "lodash";
import cloneDeep from "clone-deep";
import {
	USER_REVOKE_ENTITY,
	FORM_USERS_ENTITIES,
	USER_PERMIT_ENTITY
} from "routes.js";
import { ENTITY_TYPES } from "constants.js";
import { pluralize } from "humanize-plus";
import swal from "sweetalert2";
import {
	ENTITY_TRANSLATION_KEYS, 
	I18NextContext 
} from "../../../services/i18nService";

export function controller(
	$scope,
	$uibModal,
	$timeout,
	$logger,
	appState,
	api,
	toastr,
	entityManager,
	loadingService
) {
	const ctrl = this;

	ctrl.promiseTracker = loadingService.getTracker();
	ctrl.entityQueryConfigurations = [];

	ctrl.loadEntities = loadEntities;
	ctrl.removeEntity = removeEntity;
	ctrl.addEntity = addEntity;
	ctrl.$onInit = onInit;
	ctrl.ENTITY_TRANSLATION_KEYS = ENTITY_TRANSLATION_KEYS;

	function onInit() {
		ctrl.form = appState.forms.selected;
		ctrl.entityPermissions = cloneDeep(appState.permissions.Administration.Entity);

		const groups = {};
		for(const fieldId in entityManager.allEntityConfigs)
		{
			const eqc = entityManager.allEntityConfigs[fieldId];
			
			if(!eqc.IsUserScoped)
				continue;
			
			const key = `${eqc.Primary.Type.Code}.${eqc.Friendly.Type.Code}.${eqc.RelationshipType.Code}`;
			groups[key] = (groups[key] ?? { names: [], query: eqc });
			groups[key].names.push(I18NextContext.Instance.i18n.t(`types.${eqc.Friendly.Type.Code}.Name`));
		}

		ctrl.entityQueryConfigurations = Object.values(groups);

		loadEntities();
	}

	ctrl.setSelector = function(selector) {
		ctrl.selector = selector;
	};

	function loadEntities() {
		const assignedPromise = api.get(FORM_USERS_ENTITIES(ctrl.form.Id, ctrl.user.Id))
			.then(friendlyEntities => {
				const assignedEntities = friendlyEntities.filter(e => e.Association.Code === "PERMISSION");

				const formEntities = assignedEntities.map((l) => {
					l.Entity.RelationshipType = l.RelationshipType.Code;
					l.Entity.Type = entityManager.userScopedEntityTypes[l.Entity.Type.Code];
					l.Entity.targetType = ENTITY_TYPES[l.Type.Code]?.PluralName ?? pluralize(2, l.Type.Name ?? l.Type.Code);
					l.Entity.targetTypeCode = l.Type.Code;

					if(l.Entity.RelationshipType === "SELF") {
						const formEntity = entityManager.getEntitiesForAllFields(l.Entity.Id);
						if(formEntity && formEntity.length) {
							const thisPrimary = formEntity[0];
							const thisFriendly = !!thisPrimary.friendlyIds && [...entityManager.getEntitiesForAllFields(thisPrimary.friendlyIds[0])];

							if(thisFriendly && thisFriendly.length > 0) {
								l.Entity.friendly = thisFriendly[0];
								l.Entity.friendly.display = !!l.Entity.friendly.Name ? `from ${l.Entity.friendly.Name} (${l.Entity.friendly.Code})` : `from ${l.Entity.friendly.Code}`;
							}
						}
					}

					return l.Entity;
				});

				ctrl.hasKeys = some(formEntities, e => !!e.ExternalKey);
				ctrl.hasClassifications = some(formEntities, (e) => !!e.Classification);
				ctrl.entities = formEntities;
			})
			.catch((err) => {
				$logger.exception(err, "Exception in retrieving/loading user entities", { formId: ctrl.form.Id, userId: ctrl.user.Id });
				toastr.error("There was an error retrieving/loading user entities");
				ctrl.entities = [];
			});

		loadingService.watch(assignedPromise, "Loading...")
			.then(() => {
				ctrl.hasClassifications = some(ctrl.entities, (e) => !!e.Classification);
			});
	}

	function addEntity(entityQueryConfiguration) {
		const fakeLookup = {
			entityTypes: [entityQueryConfiguration],
			hideCode: false, 
			entityQueryConfiguration
		};

		$uibModal
			.open({
				component: "advancedSearchModal",
				size: "lg",
				resolve: {
					lookup: fakeLookup
				}
			})
			.result.then((selection) => {
				handleEntitySelection(selection, entityQueryConfiguration);
			});
	}

	function handleEntitySelection(entity, entityConfig) {
		if(entityConfig.Friendly.Type.Code === entity.Type.Code) {
			let getSelections = () => new Promise((res) => res([entity]));

			if(entityConfig.RelationshipType.Code !== "SELF") {
				getSelections = () =>
					$uibModal.open({
						component: "userFriendlyAssociation",
						size: "lg",
						resolve: {
							entity, 
							entityConfig
						}
					}).result;
			}

			getSelections()
				.then((selections = []) => {
					if(!selections.length) {
						return;
					}

					const duplicateAssociations = intersectionWith(ctrl.entities, selections, (val1, val2) => {
						return val1.Id === val2.Id && val1.RelationshipType === entityConfig.RelationshipType.Code;
					});

					if(duplicateAssociations.length > 0) {
						showDuplicateAssociationWarning(duplicateAssociations);
					}

					const newAssociations = differenceWith(selections, ctrl.entities, (val1, val2) => {
						return val1.Id === val2.Id && val2.RelationshipType === entityConfig.RelationshipType.Code;
					});

					forEach(newAssociations, selection => {
						handleEntityAssociation(selection, entityConfig)
							.then(entity => {
								assignConfigTypesToEntity(entity, entityConfig);
							});
					});
				})
				.catch((err) => {
					$logger.exception("Failure to get selections for entities", err);
					toastr.error("There was an error getting entity selections");
				});
		}
		else {
			handleEntityAssociation(entity, entityConfig).then(entity => {
				assignConfigTypesToEntity(entity, entityConfig);
			});
		}
	}

	function assignConfigTypesToEntity(entity, entityConfig) {
		if(entity.RelationshipType === undefined) {
			entity.RelationshipType = entityConfig.RelationshipType.Code;
		}
		if(entity.targetType === undefined) {
			entity.targetType = pluralize(2, entityConfig.Primary.Type.Name);
		}
		if(entity.targetTypeCode === undefined) {
			entity.targetTypeCode = entityConfig.Primary.Type.Code;
		}
	}

	function showDuplicateAssociationWarning(assns) {
		const assnsHtml = assns
			.map((entity) => {
				return `<li>${entity.Name}</li>`;
			})
			.join("");

		swal.fire({
			title: "Duplicate Associations",
			html:
				`You are attempting to make associations to entities which already exist for this user:
			<br/>
			<br/>
			<ul style="text-align:left">
			${assnsHtml}
			</ul>
			<br/>
			These associations will be ignored.`,
			showCloseButton: true,
			type: "warning",
			showCancelButton: false
		});
	}

	function handleEntityAssociation(entity, config) {
		const targetType = config.Primary.Type.Code;
		const relationshipType = targetType === entity.Type.Code ? "SELF" : config.RelationshipType.Code;

		const route = USER_PERMIT_ENTITY(entityManager.form.Id, ctrl.user.Id, entity.Id, relationshipType, targetType);

		const promise = api.post(route).then(() => {
			toastr.success(`${entity.Name || entity.Code} added.`);

			ctrl.entities.push(entity);

			if(!!entity.Classification) {
				ctrl.hasClassifications = true;
			}
			$timeout(() => {
				$scope.$apply();
			});

			return entity;
		});

		ctrl.promiseTracker.addPromise(promise);
		return loadingService.watch(promise, "Loading...");
	}

	function removeEntity(entity) {
		const targetType = entity.targetTypeCode;
		const relationshipType = entity.RelationshipType;

		const route = USER_REVOKE_ENTITY(ctrl.form.Id, ctrl.user.Id, entity.Id, relationshipType, targetType);
		const promise = api.put(route).then(() => {
			toastr.success(`${entity.Code} removed.`);

			remove(ctrl.entities, possibleRemove => possibleRemove.Id === entity.Id && possibleRemove.RelationshipType === relationshipType);

			$timeout(() => {
				$scope.$apply();
			});
		});

		loadingService.watch(promise, "Loading...");
	}

	ctrl.viewEntity = (entity) => {
		const appEntity = entityManager.getEntitiesForAllFields(entity.Id);

		if(appEntity.length === 0) {
			toastr.error("The selected entity is not assigned to this form.");
		}
		else {
			$uibModal
				.open({
					component: "entity-modal",
					size: "lg",
					resolve: {
						entity: () => appEntity[0],
						typeConfig: () => ctrl.form.Agency.Types.find(t => t.Code == entity.Type.Code),
						permissions: () => ctrl.entityPermissions,
						syncUrl: () => false
					}
				})
				.result.then(() => { });
		}
	};
}

export default angular.module("vesl.components.user-entities", []).component("userEntities", {
	controller,
	template,
	bindings: {
		user: "<"
	}
}).name;
